import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Card from '../../../components/ui/Card';
import UnsavedChangesBar from '../../../components/ui/UnsavedChangesBar';
import { listRoles } from '../../../api/roles';
import { syncUserRoles } from '../../../api/users';
import { pushToast } from '../../../components/ui/toastBus';
import { usePermission } from '../../../auth/usePermission';

export default function RolesTab({ user }) {
  const { can } = usePermission();
  const readOnly = !can('permissions.assign');
  const queryClient = useQueryClient();
  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: listRoles });

  const [selected, setSelected] = useState(user.roles);

  useEffect(() => setSelected(user.roles), [user.roles]);

  const dirty = JSON.stringify([...selected].sort()) !== JSON.stringify([...user.roles].sort());

  const mutation = useMutation({
    mutationFn: () => syncUserRoles(user.id, selected),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Roles updated.' });
      queryClient.invalidateQueries({ queryKey: ['users', String(user.id)] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not update roles.' }),
  });

  const toggle = (name) => {
    if (readOnly) return;
    setSelected((prev) => (prev.includes(name) ? prev.filter((r) => r !== name) : [...prev, name]));
  };

  return (
    <div className="space-y-4">
      <Card className="max-w-lg">
        <div className="space-y-2">
          {(rolesQuery.data || []).map((role) => (
            <label
              key={role.id}
              className={`flex items-center justify-between rounded px-2 py-1.5 text-sm ${
                readOnly ? 'text-gray-500' : 'text-gray-700 cursor-pointer hover:bg-gray-50'
              }`}
            >
              <span className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className={readOnly ? 'accent-gray-400' : 'accent-primary'}
                  checked={selected.includes(role.name)}
                  disabled={readOnly}
                  onChange={() => toggle(role.name)}
                />
                {role.name}
              </span>
              <span className="text-xs text-gray-400">{role.permissions_count} permissions</span>
            </label>
          ))}
        </div>
      </Card>

      {!readOnly && (
        <UnsavedChangesBar
          dirty={dirty}
          saving={mutation.isPending}
          onSave={() => mutation.mutate()}
          onDiscard={() => setSelected(user.roles)}
        />
      )}
    </div>
  );
}
